import { mapState, mapGetters } from 'vuex'
import KeybeAvatar from '@/shared/infrastructure/ui/KeybeAvatar.vue'
import AppsUseCases from '@/modules/Apps/application/AppsUseCases'
import AppsController from '@/modules/Apps/infrastructure/controllers/Apps.controller'
import EmitToast from '@/utils/EmitToast'
import { appCreatorCompany, appsGetter, categoriesGetter, dataToCreateAppByCompany, dataToGetApps } from '@/modules/Apps/domain/AppsController'
import { KeybeIconType } from '@/shared/infrastructure/enums/icons/KeybeIconType'
import KeybeIcon from '@/shared/infrastructure/ui/icons/KeybeIcon.vue'
import { UserApp } from '@/modules/Config/GeneralUserConfiguration/domain/LoggedUser'
import UserUseCases from '@/modules/Config/GeneralUserConfiguration/application/UserUseCases'
import { dataToGetUser } from '@/modules/Config/GeneralUserConfiguration/domain/UserController'
import UserController from '@/modules/Config/GeneralUserConfiguration/infrastructure/controller/User.controller'
import KBButtonLoader from '@/shared/infrastructure/ui/progress/KBButtonLoader/KBButtonLoader.vue'
import KeybeText from '@/modules/DesignSystem/infrastructure/components/KeybeText/KeybeText.vue'
import KeybeAutocomplete from '@/modules/DesignSystem/infrastructure/components/KeybeAutocomplete/KeybeAutocomplete/KeybeAutocomplete.vue'
import KeybeButton from '@/modules/DesignSystem/infrastructure/components/KeybeButton/KeybeButton.vue'
import Colors from '@/utils/Colors'
import { CDPFieldsEnums } from '@/modules/CDP/shared/contacts/domain/enums/CDPFieldsEnums'
export default {
  components: {
    KeybeAvatar,
    KeybeIcon,
    KBButtonLoader,
    KeybeText,
    KeybeAutocomplete,
    KeybeButton
  },
  computed: {
    ...mapState('UserStore', ['token', 'user', 'isOwner', 'isUserReadonly']),
    ...mapGetters('UserStore', ['getUserApps', 'isAdmin', 'isSuperAdmin']),
    ...mapState('AppStore', [CDPFieldsEnums.LANGUAGE, 'isKeybeApp', 'selectedApp']),
    accountLabel (): string {
      return this.addAccount ? this.$t('createNewAccount') : this.$t('accounts')
    },
    dataEmpty (): boolean {
      return !this.newAppName || !this.newAppCategory || !this.newAppRelatedPeople || (this.newAppCategory === CDPFieldsEnums.OTHER && !this.newAppCategoryOther)
    },
    canAddApp (): boolean {
      return this.isOwner || this.isAdmin || this.isSuperAdmin
    }
  },
  mounted () {
    this.getInitial()
    this.getCategories()
  },
  data () {
    return {
      Colors,
      CDPFieldsEnums,
      userApps: [],
      addAccount: false,
      loading: false,
      loadingCreate: false,
      appsController: new AppsController(),
      userController: new UserController(),
      KeybeIconType,
      newAppName: '',
      newAppCategory: '',
      newAppRelatedPeople: '',
      newAppCategoryOther: '',
      brandSizeItems: [
        {
          value: '70',
          label: this.$t('under70')
        },
        {
          value: '71-100',
          label: this.$t('between71')
        },
        {
          value: '101-350',
          label: this.$t('between101')
        },
        {
          value: '351-500',
          label: this.$t('between351')
        },
        {
          value: '501-1000',
          label: this.$t('between501')
        },
        {
          value: '1001-1700',
          label: this.$t('between1001')
        }
      ],
      categories: []
    }
  },
  methods: {
    async getCategories (): Promise<void> {
      const response: categoriesGetter = await AppsUseCases.getCategories(this.appsController)
      if (response.status) {
        const categories: string[] = response?.data

        categories.forEach((category: string) => {
          if (category !== CDPFieldsEnums.KEYBE) {
            this.categories.push({
              text: this.$t(category),
              value: category
            })
          }
        })
        this.categories.push({
          text: this.$t(CDPFieldsEnums.OTHER),
          value: CDPFieldsEnums.OTHER
        })
        if (this.isKeybeApp) {
          this.categories.push({
            text: this.$t(CDPFieldsEnums.KEYBE),
            value: CDPFieldsEnums.KEYBE
          })
        }
      } else EmitToast.emitErrors(response.message)
    },
    async getInitial (): Promise<void> {
      await this.getUser()
      await this.getApps()
    },
    openAddAccount (): void {
      if (this.isUserReadonly) return
      this.addAccount = true
    },
    appName (app: {[key: string]: any}): string {
      return app?.name
    },
    appRole (app: {[key: string]: any}): string {
      return this.$t(app?.role)
    },
    async getUser (): Promise<void> {
      try {
        if (!this.user?.uuid) return
        this.loading = true
        const data: dataToGetUser = {
          uuid: this.user.uuid,
          token: this.token
        }
        await UserUseCases.getUser(this.userController, data)
      } catch (e) {
        console.log(e)
      }
    },
    async getApps (): Promise<void> {
      try {
        this.loading = true
        const data: dataToGetApps = {
          token: this.token,
          search: '',
          offset: 0,
          limit: 10
        }
        const response: appsGetter = await AppsUseCases.getApps(this.appsController, data)
        if (!Array.isArray(response)) {
          EmitToast.emitErrors(response)
          return
        }

        this.getUserApps.forEach((userApp: UserApp) => {
          response.forEach(
            (
              app: {
                name: string,
                uuid: string
              }
            ) => {
              if (userApp.appUUID === app.uuid) {
                this.userApps.push({
                  uuid: app.uuid,
                  name: app.name,
                  isOwner: userApp.isOwner,
                  role: userApp.role
                })
              }
            })
        })
        this.loading = false
      } catch (e) {
        console.error(e)
      }
    },
    async createNewApp (): Promise<void> {
      this.loadingCreate = true
      const data: dataToCreateAppByCompany = {
        token: this.token,
        createInput: {
          app: {
            name: this.newAppName,
            categories: [
              this.newAppCategory
            ],
            relatedPeople: this.newAppRelatedPeople,
            lang: this.language,
            version: 'v6',
            country: this.selectedApp?.country || 'US',
            timeZone: this.selectedApp?.timeZone || 'America/Bogota'
          },
          user: {
            uuid: this.user?.uuid,
            companyUUID: this.selectedApp?.company?.uuid
          }
        }
      }

      if (this.newAppCategory === CDPFieldsEnums.OTHER && this.newAppCategoryOther) {
        data.createInput.app.categories = [
          this.newAppCategoryOther
        ]
      }

      const response: appCreatorCompany = await AppsUseCases.createAppByCompany(this.appsController, data)

      if (response.status) {
        EmitToast.emitSuccess(this.$t('appCreatedSuccessfully'))
        this.closeAddAccount()
      } else EmitToast.emitErrors(response.message)
      this.loadingCreate = false
    },
    closeAddAccount (): void {
      this.addAccount = false
      this.newAppName = ''
      this.newAppCategory = ''
      this.newAppRelatedPeople = ''
      this.userApps = []
      this.getInitial()
    }
  },
  beforeDestroy () {
    AppsUseCases.abort(this.appsController)
  }
}
